/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaWorkspaceToolbar.tsx
 * FILE_ID: JOBHUB-QA-WORKSPACE-TOOLBAR-001
 * PURPOSE: One sticky bar: selection, undo/redo, ITP export, modes.
 *      No All checklists toggle on desktop (operator 2026-09-23: it read as
 *      a second selection control). Phone keeps its More-sheet row.
 * LANDMINE: Nothing in this bar may be absolute or fixed. Mode pills live here
 *      so they cannot scroll away from the work surface.
 */
import type { ReactNode } from 'react'
import { useState } from 'react'
import { CheckCircle2, Keyboard, LayoutGrid, Redo2, Undo2 } from 'lucide-react'
import { QaPhotoDisplayControls, useIsLgUp, useIsMdUp } from '@quotech/ui'
import { QaShortcutsDialog } from './QaShortcutsDialog'
import { JobQaModeToggle, type QaAreaMode } from '../JobQaModeToggle'
import { QaAddMenuButton } from './QaAddMenuButton'
import { JobQaWorkspaceActions } from './JobQaWorkspaceActions'
import type { JobQaTabController } from './useJobQaTabController'

export function JobQaWorkspaceToolbar({
  controller,
  areasWidth,
  areasCollapsed = false,
  onToggleAreas,
  checklistsCollapsed = false,
  onToggleChecklists,
  onStartRename,
  onOpenTemplates,
  onModeChange,
  onSaveToLibrary,
  itpActions,
}: {
  controller: JobQaTabController
  areasWidth: number
  /** Omitted callers stay expanded. The workspace section always passes this. */
  areasCollapsed?: boolean
  onToggleAreas?: () => void
  /** True only when the desktop checklists column is collapsed. */
  checklistsCollapsed?: boolean
  onToggleChecklists?: () => void
  onStartRename: (scope: 'areas' | 'checklists', id: string) => void
  onOpenTemplates: () => void
  onModeChange: (mode: QaAreaMode) => void
  onSaveToLibrary?: () => void
  itpActions?: ReactNode
}) {
  const { history, workspace } = controller
  const canBuild = controller.canBuild !== false
  const isMdUp = useIsMdUp()
  const isLgUp = useIsLgUp()
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  return (
    <div className="sticky top-0 z-30 flex min-w-0 flex-wrap items-center gap-2 border-b border-border bg-card px-1 py-1.5">
      {workspace.areaMode !== 'build' ? null : (
      <div
        className="flex shrink-0 items-center gap-1 px-2 py-1.5"
        style={{ width: areasCollapsed ? 'auto' : areasWidth }}
      >
        <p className="min-w-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Locations
        </p>
        <button
          type="button"
          className="shrink-0 px-0.5 text-xs font-semibold leading-none text-muted-foreground hover:text-foreground"
          aria-expanded={!areasCollapsed}
          aria-label={areasCollapsed ? 'Expand locations' : 'Collapse locations'}
          title={areasCollapsed ? 'Expand locations' : 'Collapse locations'}
          data-testid="qa-toggle-locations"
          onClick={() => onToggleAreas?.()}
        >
          {areasCollapsed ? '>' : 'v'}
        </button>
      </div>
      )}
      {workspace.areaMode === 'build' && isLgUp && checklistsCollapsed ? (
        <div className="flex shrink-0 items-center gap-1 px-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Checklists
          </p>
          <button
            type="button"
            className="shrink-0 px-0.5 text-xs font-semibold leading-none text-muted-foreground hover:text-foreground"
            aria-expanded={false}
            aria-label="Expand checklists"
            title="Expand checklists"
            data-testid="qa-toggle-checklists"
            onClick={() => onToggleChecklists?.()}
          >
            {'>'}
          </button>
        </div>
      ) : null}
      <JobQaWorkspaceActions
        controller={controller}
        onStartRename={onStartRename}
      />
      <QaPhotoDisplayControls />
      {history && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={!history.canUndo}
            onClick={() => void history.undo()}
            title={history.lastLabel ? `Undo ${history.lastLabel} (Ctrl/Cmd+Z)` : 'Undo (Ctrl/Cmd+Z)'}
            aria-label="Undo"
            data-testid="qa-history-undo"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            disabled={!history.canRedo}
            onClick={() => void history.redo()}
            title="Redo (Ctrl/Cmd+Shift+Z)"
            aria-label="Redo"
            data-testid="qa-history-redo"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <Redo2 className="h-3.5 w-3.5" aria-hidden />
          </button>
          {isMdUp && (
            <button
              type="button"
              aria-label="Shortcuts"
              data-testid="qa-shortcuts"
              onClick={() => setShortcutsOpen(true)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground"
            >
              <Keyboard className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      )}
      {isMdUp && (
        <QaShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      )}
      {canBuild && workspace.areaMode === 'build' && (
        <button
          type="button"
          data-testid="qa-open-templates"
          onClick={onOpenTemplates}
          className="inline-flex min-h-[32px] items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium"
        >
          <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" /> Templates
        </button>
      )}
      {canBuild && workspace.areaMode === 'build' && (
        <QaAddMenuButton controller={controller} />
      )}
      {canBuild && workspace.areaMode === 'build' && (
        <button
          type="button"
          data-testid="qa-finish-build"
          disabled={workspace.leavingBuild}
          onClick={() => void workspace.leaveBuild('run')}
          className="inline-flex min-h-[32px] items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium"
        >
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          {workspace.leavingBuild ? 'Saving...' : 'Finish build'}
        </button>
      )}
      <JobQaModeToggle
        mode={workspace.areaMode}
        onChange={onModeChange}
        showBuild={canBuild}
        onSaveToLibrary={onSaveToLibrary}
        actions={workspace.areaMode === 'itp' ? itpActions : undefined}
      />
    </div>
  )
}
